import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  Grid,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { isArray, isString } from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';

import { ClientUrlParamsDynamicValues, ClientUrlParamsUpdateInput } from 'api';
import { ValidatedTextField } from 'components';

import ValueOptions from './ValueOptions';
import theme from 'theme';

const styles = {
  dynamicValueSelect: {
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },

  input: {
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: 'transparent',
  },
};

enum UrlValueTypes {
  Value = 'value',
  DynamicValue = 'dynamic_value',
  ValueOptions = 'value_options',
}

interface IProps {
  index: number;
  onDelete: () => void;
}

const TrackingKey: FC<IProps> = ({ index, onDelete }) => {
  const formik = useFormikContext<ClientUrlParamsUpdateInput>();
  const [prevValueOptions, setPrevValueOptions] = useState([]);
  const { t } = useTranslation();

  const { values, setFieldValue } = formik;
  const config = values.configuration[index];
  const valueType = getValueType();

  const keyField = `configuration[${index}].key`;
  const staticValueField = `configuration[${index}].value`;
  const dynamicValueField = `configuration[${index}].dynamic_value`;
  const valueOptionsField = `configuration[${index}].value_options`;
  const defaultOptionField = `configuration[${index}].default_option_value`;

  function getValueType() {
    if (isString(config.value)) {
      return UrlValueTypes.Value;
    }

    if (isString(config.dynamic_value)) {
      return UrlValueTypes.DynamicValue;
    }

    if (isArray(config.value_options)) {
      return UrlValueTypes.ValueOptions;
    }

    return '';
  }

  // TODO: Clean this up
  function handleChangeValueType(event: SelectChangeEvent<string>) {
    const { target } = event;
    const { value } = target;

    if (value === UrlValueTypes.ValueOptions) {
      setFieldValue(valueOptionsField, prevValueOptions);
      if (prevValueOptions.length) {
        setFieldValue(defaultOptionField, prevValueOptions[0]);
      }
    } else {
      if (config.value_options) {
        setPrevValueOptions(config.value_options);
      }
      setFieldValue(valueOptionsField, null);
      setFieldValue(defaultOptionField, null);
    }

    setFieldValue(staticValueField, value === UrlValueTypes.Value ? '' : null);
    setFieldValue(
      dynamicValueField,
      value === UrlValueTypes.DynamicValue ? ClientUrlParamsDynamicValues.User : null
    );
  }

  function renderStaticValue() {
    return (
      <ValidatedTextField
        name={staticValueField}
        i18nKey="parameters:trackingStaticValuePlaceholder"
        TextFieldProps={{ label: null, fullWidth: true }}
        InputProps={{ className: styles.input }}
      />
    );
  }

  function renderDynamicValue() {
    return (
      <Field name={dynamicValueField}>
        {({ field }: FieldProps) => (
          <FormControl variant="standard" fullWidth margin="normal">
            <Select
              variant="standard"
              displayEmpty
              value={field.value}
              onChange={field.onChange}
              sx={{ select: styles.dynamicValueSelect }}
              input={<Input name={dynamicValueField} sx={styles.input} />}
            >
              <MenuItem value={ClientUrlParamsDynamicValues.User}>
                {t('parameters:trackingDynamicValueUserOption')}
              </MenuItem>
              <MenuItem value={ClientUrlParamsDynamicValues.Network}>
                {t('parameters:trackingDynamicValueNetworkOption')}
              </MenuItem>
              <MenuItem value={ClientUrlParamsDynamicValues.ShareId}>
                {t('parameters:trackingDynamicValueShareIdOption')}
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </Field>
    );
  }

  function renderValueTypeInput() {
    switch (valueType) {
      case UrlValueTypes.Value:
        return renderStaticValue();
      case UrlValueTypes.DynamicValue:
        return renderDynamicValue();
      case UrlValueTypes.ValueOptions:
        return <ValueOptions index={index} />;
    }

    return null;
  }

  return (
    <Grid container alignItems="center">
      <Grid xs={3}>
        <Typography variant="subtitle2">{t('parameters:trackingKey')}</Typography>
      </Grid>
      <Grid xs={6}>
        <ValidatedTextField
          name={keyField}
          i18nKey="parameters:trackingKeyPlaceholder"
          TextFieldProps={{ margin: 'none', label: null }}
        />
      </Grid>
      <Grid xs sx={{ display: 'flex' }} justifyContent="flex-end">
        <Button onClick={onDelete} endIcon={<DeleteIcon />}>
          {t('common:delete')}
        </Button>
      </Grid>

      <Grid container alignItems="center" sx={{ marginTop: 2 }}>
        <Grid xs={3}>
          <Typography variant="subtitle2">{t('parameters:trackingValue')}</Typography>
        </Grid>
        <Grid xs={6}>
          <Select
            variant="standard"
            fullWidth
            displayEmpty
            value={valueType}
            onChange={handleChangeValueType}
          >
            <MenuItem value="" disabled>
              {t('parameters:trackingValuePlaceholder')}
            </MenuItem>
            <MenuItem value="value">{t('parameters:trackingStaticValue')}</MenuItem>
            <MenuItem value="dynamic_value">{t('parameters:trackingDynamicValue')}</MenuItem>
            <MenuItem value="value_options">{t('parameters:trackingValueOptions')}</MenuItem>
          </Select>
        </Grid>
        <Grid xs={3} />
      </Grid>

      <Grid container alignItems="center">
        <Grid xs={3} />
        <Grid xs={6}>{renderValueTypeInput()}</Grid>
      </Grid>
    </Grid>
  );
};

export default TrackingKey;
